import {Box} from '@viptech/react-components'

import {startOfYear, sub} from 'date-fns'
import moment from 'moment'
import {useEffect, useState} from 'react'
import {VerifyUserAndCompanyPermission} from 'src/common/utils/ValidatePermissions'
import {useCompany} from 'src/contexts/CompanyContext'
import {CompaniesIdsOnSekronThemeWithDashboardDefaultEnum, CompaniesWithPersonalizedDashboards} from 'src/hooks/usePersonalizedThemeByCompany'
import DashboardHeader from 'src/pages/dashboard/components/DashboardHeader'
import {RangeOfDates} from 'src/pages/dashboard/components/PersonalizedDatesRange'
import EventsDashboard from 'src/pages/dashboard/default-dashboard/EventsDashboard'
import AnaliticDashboard from 'src/pages/dashboard/personalized-dashboards/AnaliticDashboard'
import PersonalizedHeader from 'src/pages/dashboard/personalized-dashboards/PersonalizedHeader'
import SyntheticDashboard from 'src/pages/dashboard/personalized-dashboards/SyntheticDashboard'
import DetectionsDashboard from './default-dashboard/DetectionsDashboard'

export type Filters = {
  cameraIds: Array<number>
  clientIds: Array<number>
  eventStatuses: Array<string>
  start: string
  end: string
  typeHistogram?: string
}

export default function Dashboard() {
  const url = window.location.href

  const {selectedCompanyId, companies} = useCompany()
  const RenderPersonalizedHeader =
    CompaniesWithPersonalizedDashboards.some((companyUrl) => url.includes(companyUrl)) &&
    selectedCompanyId !== CompaniesIdsOnSekronThemeWithDashboardDefaultEnum.SWIFT

  const RenderAnalyticDashboardOption = companies.find((companie) => companie.id === selectedCompanyId)?.showAnalyticDashboard ?? false
  const [dashboardSelected, setDashboardSelected] = useState<'events' | 'detections' | 'analytic' | 'synthetic'>('detections')

  const dashboardsOptionsDefault: Array<{id: 'events' | 'detections' | 'analytic'; label: string}> = [
    {id: 'detections', label: 'Detecções'},
    {id: 'events', label: 'Eventos'},
  ]
  const dashboardOptionsWithAnalitic: Array<{id: 'events' | 'detections' | 'analytic'; label: string}> = [
    {id: 'detections', label: 'Detecções'},
    {id: 'events', label: 'Eventos'},
    {id: 'analytic', label: 'Analítico'},
  ]

  const isLprEnabled = VerifyUserAndCompanyPermission([selectedCompanyId], 'lpr:read')
  const isFacialEnabled = VerifyUserAndCompanyPermission([selectedCompanyId], 'face:read')
  const isCountingObjectsEnable = VerifyUserAndCompanyPermission([selectedCompanyId], 'detector-counter:read')
  const isHeatmapEnable = VerifyUserAndCompanyPermission([selectedCompanyId], 'heatmap:read')

  const [filter, setFilter] = useState<Filters>({
    cameraIds: [],
    clientIds: [],
    eventStatuses: [],
    start: moment(new Date(new Date().setHours(0, 0, 0, 0))).format(),
    end: moment(new Date(new Date().setHours(23, 59, 59, 999))).format(),
    typeHistogram: 'day',
  })

  const initialRangeDate = () => {
    const today = new Date()
    const thisDayLastYear = sub(today, {years: 1})
    const actual = {
      start: moment(startOfYear(today)).format(),
      end: moment(today.toLocaleString()).format(),
    }

    const last = {
      start: moment(startOfYear(thisDayLastYear)).format(),
      end: moment(thisDayLastYear).format(),
    }
    return {actual, last}
  }

  const [rangeDatesFilter, setRangeDatesFilter] = useState<RangeOfDates>(initialRangeDate())
  const [labelOnFilter, setLabelOnFilter] = useState('')

  useEffect(() => {
    if (RenderPersonalizedHeader) setDashboardSelected('analytic')
    else setDashboardSelected('detections')
  }, [RenderPersonalizedHeader])

  return (
    <Box display='flex' direction='column' flex={1} rowGap='20px'>
      {RenderPersonalizedHeader ? (
        <PersonalizedHeader
          dashboardSelected={dashboardSelected}
          setDashboardSelected={setDashboardSelected}
          initialDates={[new Date(new Date().setHours(0, 0, 0, 0)), new Date(new Date().setHours(23, 59, 59, 999))]}
          filter={filter}
          onChangeFilter={(start, end, clientIds, cameraIds) => {
            setFilter((prev) => {
              return {
                ...prev,
                cameraIds: cameraIds ?? [],
                clientIds: clientIds ?? [],
                start: moment(new Date(start)).format(),
                end: moment(new Date(end)).format(),
              }
            })
          }}
          setRangeDatesFilter={setRangeDatesFilter}
          setLabelOnFilter={setLabelOnFilter}
        />
      ) : (
        <DashboardHeader
          dashboardSelected={dashboardSelected}
          setDashboardSelected={setDashboardSelected}
          initialDates={[new Date(new Date().setHours(0, 0, 0, 0)), new Date(new Date().setHours(23, 59, 59, 999))]}
          filter={filter}
          onChangeFilter={(start, end, clientIds, cameraIds, eventStatuses) => {
            setFilter({
              cameraIds: cameraIds ?? [],
              clientIds: clientIds ?? [],
              eventStatuses: eventStatuses ?? [],
              start: moment(new Date(start)).format(),
              end: moment(new Date(end)).format(),
              typeHistogram: filter.typeHistogram,
            })
          }}
          dashboardsOptions={RenderAnalyticDashboardOption ? dashboardOptionsWithAnalitic : dashboardsOptionsDefault}
        />
      )}

      {dashboardSelected === 'detections' && (
        <DetectionsDashboard
          filter={filter}
          isFacialEnabled={isFacialEnabled}
          isLprEnabled={isLprEnabled}
          isCountingObjectsEnable={isCountingObjectsEnable}
          isHeatmapEnable={isHeatmapEnable}
        />
      )}
      {dashboardSelected === 'events' && <EventsDashboard filter={filter} isFacialEnabled={isFacialEnabled} isLprEnabled={isLprEnabled} />}
      {dashboardSelected === 'analytic' && (
        <AnaliticDashboard
          filter={filter}
          onChangeFilter={(typeHistogram) =>
            setFilter((prev: any) => {
              return {
                ...prev,
                typeHistogram,
              }
            })
          }
        />
      )}
      {dashboardSelected === 'synthetic' && <SyntheticDashboard filter={rangeDatesFilter} labelOnFilter={labelOnFilter} />}
    </Box>
  )
}
